import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';
import { services, reviews, certifications } from '../../mocks/services';
import EmergencyModal from '../../components/feature/EmergencyModal';

export interface AreaPageProps {
  area: string;
  areaSlug: string;
  state: string;
  county: string;
  population: string;
  description: string;
  neighborhoods: string[];
  nearbyAreas: { name: string; slug: string }[];
  faqs: { q: string; a: string }[];
  heroImage: string;
  heroImageAlt: string;
  teamImage: string;
  teamImageAlt: string;
  schema: object;
}

const AreaTemplate = ({
  area,
  areaSlug,
  state,
  county,
  population,
  description,
  neighborhoods,
  nearbyAreas,
  faqs,
  heroImage,
  heroImageAlt,
  teamImage,
  teamImageAlt,
  schema,
}: AreaPageProps) => {
  const [currentReview, setCurrentReview] = useState(0);
  const [glowIndex, setGlowIndex] = useState(-1);
  const [showEmergency, setShowEmergency] = useState(false);
  const brandText = `ELECTRICIAN SOLUTIONS ${area.toUpperCase()}`;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setGlowIndex(i);
      i++;
      if (i >= brandText.length) {
        i = 0;
        setTimeout(() => setGlowIndex(-1), 300);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [brandText.length]);

  const nextReview = () => setCurrentReview((p) => (p + 1) % reviews.length);
  const prevReview = () => setCurrentReview((p) => (p - 1 + reviews.length) % reviews.length);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      formBody.append('area', area);
      const response = await fetch('https://readdy.ai/api/form/d6ontcl39lnhn4hh1e30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <>
      <SEOHead
        title={`Electrician in ${area}, ${state} | Licensed ${area} Electrician`}
        description={`Licensed electrician serving ${area}, ${state}. C-10 certified electrical contractor for residential & commercial work in ${area} and ${county}. Free quotes, 24/7 emergency service.`}
        canonical={`/areas/${areaSlug}`}
        keywords={`electrician ${area}, ${area} electrician, electrical services ${area} CA, licensed electrician ${area}, emergency electrician ${area}`}
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <style>{`
          @keyframes electricFlicker {
            0%, 100% { opacity: 1; text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500; }
            25% { opacity: 0.85; text-shadow: 0 0 4px #FFD700, 0 0 10px #FFD700; }
            50% { opacity: 1; text-shadow: 0 0 12px #FFD700, 0 0 30px #FFD700, 0 0 60px #FFA500, 0 0 80px #FF8C00; }
            75% { opacity: 0.9; text-shadow: 0 0 6px #FFD700, 0 0 15px #FFD700; }
          }
          @keyframes boltPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
          }
          @keyframes callPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.5); }
            50% { box-shadow: 0 0 0 10px rgba(255, 215, 0, 0); }
          }
          .electric-char { display: inline-block; transition: all 0.1s ease; color: #FFD700; }
          .electric-char.lit { animation: electricFlicker 0.3s ease-in-out; color: #fff; text-shadow: 0 0 8px #FFD700, 0 0 20px #FFD700, 0 0 40px #FFA500; }
          .bolt-icon { animation: boltPulse 1.2s ease-in-out infinite; }
          .call-banner { animation: callPulse 2s ease-in-out infinite; }
        `}</style>

        <Navbar />

        {/* Hero */}
        <section className="relative min-h-screen flex items-center pt-24 md:pt-20">
          <div className="absolute inset-0 z-0">
            <img src={heroImage} alt={heroImageAlt} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/45"></div>
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 w-full py-16 md:py-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-5 flex-nowrap overflow-hidden">
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon">
                    <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
                  </span>
                  <span className="font-black tracking-[0.1em] sm:tracking-[0.15em] text-[8px] sm:text-xs select-none whitespace-nowrap">
                    {brandText.split('').map((char, i) => (
                      <span key={i} className={`electric-char${glowIndex === i ? ' lit' : ''}`}>
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))}
                  </span>
                  <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bolt-icon" style={{ animationDelay: '0.6s' }}>
                    <i className="ri-flashlight-fill text-[#FFD700] text-sm"></i>
                  </span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 sm:mb-6 leading-tight">
                  {area.toUpperCase()}&apos;S<br />LICENSED<br />ELECTRICIANS
                </h1>
                <p className="text-base sm:text-xl text-gray-200 mb-6 sm:mb-8 font-light">
                  {area}, {state}&apos;s Trusted C-10 Certified Electrical Contractor — Serving {area} &amp; {county}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                  <a href="#get-quote" className="bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all hover:scale-105 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                    Get Free Quote
                    <i className="ri-arrow-right-line"></i>
                  </a>
                  <button
                    onClick={() => setShowEmergency(true)}
                    className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-white hover:text-[#1a1a1a] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-alarm-warning-line"></i>
                    24/7 Emergency
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-white text-xs sm:text-sm uppercase tracking-wider">
                  <span className="font-bold">Licensed</span>
                  <span className="text-[#FFD700]">•</span>
                  <span className="font-bold">Insured</span>
                  <span className="text-[#FFD700]">•</span>
                  <span className="font-bold">5-Star Rated</span>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <div className="absolute bottom-8 right-8 bg-white rounded-full p-6">
                  <div className="text-center">
                    <div className="text-[#FFD700] text-4xl font-black">C-10</div>
                    <div className="text-[#1a1a1a] text-xs font-bold uppercase">Licensed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Area Intro */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                  {area}, {state}&apos;s #1 Electrician
                </span>
                <h2 className="text-3xl sm:text-4xl font-black text-[#1a1a1a] mb-5 leading-tight">
                  Your Local {area} Electrician — <span className="text-[#FFD700]">Licensed, Trusted &amp; Ready</span>
                </h2>
                <p className="text-gray-600 text-base mb-4 leading-relaxed">{description}</p>
                <p className="text-gray-600 text-base mb-6 leading-relaxed">
                  Whether you need a panel upgrade, EV charger installation, smart home wiring, or a 24/7 emergency electrician in {area}, our NECA-certified team is on call. We&apos;ve served <strong>500+ Fresno-area homeowners and businesses</strong> including hundreds of satisfied customers in {area} with a 4.9-star rating.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: 'ri-shield-check-fill', label: 'C-10 Licensed' },
                    { icon: 'ri-award-fill', label: 'NECA Certified' },
                    { icon: 'ri-time-fill', label: '24/7 Emergency' },
                    { icon: 'ri-map-pin-fill', label: `${area}, ${state}` },
                  ].map((badge) => (
                    <div key={badge.label} className="flex items-center gap-2 bg-[#f5f5f5] px-4 py-2 rounded-full">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${badge.icon} text-[#FFD700] text-base`}></i>
                      </span>
                      <span className="text-sm font-bold text-[#1a1a1a]">{badge.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden h-72 sm:h-96">
                <img src={teamImage} alt={teamImageAlt} className="w-full h-full object-cover object-top" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <p className="text-white font-bold text-lg">Proudly Serving {area} Since 2010</p>
                  <p className="text-[#FFD700] text-sm font-medium">Central Valley&apos;s Most Trusted Electrician</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 sm:mb-12 gap-4">
              <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a]">OUR SERVICES IN {area.toUpperCase()}</h2>
              <Link to="/services/residential-electrical" className="text-[#FFD700] font-bold hover:underline flex items-center gap-2 cursor-pointer whitespace-nowrap">
                View All Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
                >
                  <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                  <div className="absolute top-4 sm:top-6 left-4 sm:left-6 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                    <i className={`${service.icon} text-3xl sm:text-4xl text-[#FFD700]`}></i>
                  </div>
                  <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-2">{service.name}</h3>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-[#FFD700] text-sm font-bold flex items-center gap-2">
                      Learn More
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#FFD700] rounded-xl transition-all"></div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Neighborhoods Served */}
        <section className="py-12 sm:py-20 bg-[#f5f5f5]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a] text-center mb-8 sm:mb-12">
              SERVING {area.toUpperCase()} &amp; SURROUNDINGS
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div className="relative h-64 sm:h-96 rounded-xl overflow-hidden">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodeURIComponent(area + ', CA')}`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`${area} Service Area Map`}
                ></iframe>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a1a1a] mb-4 sm:mb-6">Neighborhoods &amp; Areas We Cover</h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                  {neighborhoods.map((n, i) => (
                    <div key={i} className="flex items-center gap-2 sm:gap-3">
                      <i className="ri-map-pin-fill text-[#FFD700] text-lg sm:text-xl"></i>
                      <span className="text-gray-700 font-medium text-sm sm:text-base">{n}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 sm:p-6 bg-white rounded-lg">
                  <p className="text-gray-600 text-sm sm:text-base mb-4">
                    Our licensed electricians cover all of {area} and {county}. From residential neighborhoods to commercial districts, we&apos;re your local electrical experts.
                  </p>
                  <a href="#get-quote" className="text-[#FFD700] font-bold hover:underline cursor-pointer text-sm sm:text-base">
                    Get a Free {area} Quote →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-12 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 sm:mb-12 gap-4">
              <h2 className="text-2xl sm:text-4xl font-black text-white">
                TRUSTED BY {area.toUpperCase()} HOMEOWNERS
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-[#FFD700] text-xl sm:text-2xl"></i>
                  ))}
                </div>
                <span className="text-[#FFD700] text-2xl sm:text-3xl font-black">4.9/5.0</span>
              </div>
            </div>
            <div className="relative px-8 sm:px-12">
              <div className="overflow-hidden">
                <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentReview * 100}%)` }}>
                  {reviews.map((review) => (
                    <div key={review.id} className="w-full flex-shrink-0">
                      <div className="bg-white rounded-2xl p-6 sm:p-10 max-w-4xl mx-auto">
                        <div className="flex gap-1 mb-4">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i key={star} className="ri-star-fill text-[#FFD700] text-lg sm:text-xl"></i>
                          ))}
                        </div>
                        <p className="text-gray-700 text-base sm:text-lg italic mb-4 sm:mb-6 leading-relaxed">&quot;{review.text}&quot;</p>
                        <div className="flex items-center gap-3 sm:gap-4">
                          <img src={review.avatar} alt={review.name} className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover" />
                          <div>
                            <div className="font-bold text-[#1a1a1a] text-sm sm:text-base">{review.name}</div>
                            {review.verified && (
                              <div className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-bold px-3 py-1 rounded-full mt-1">
                                Verified Customer
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={prevReview} className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-[#FFD700] text-[#FFD700] rounded-full hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-all cursor-pointer">
                <i className="ri-arrow-left-line text-lg sm:text-xl"></i>
              </button>
              <button onClick={nextReview} className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border-2 border-[#FFD700] text-[#FFD700] rounded-full hover:bg-[#FFD700] hover:text-[#1a1a1a] transition-all cursor-pointer">
                <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
              </button>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="get-quote" className="py-12 sm:py-20 bg-[#1a1a1a] border-t-4 border-[#FFD700]">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-5xl font-black text-white mb-4">GET A FREE QUOTE IN {area.toUpperCase()}</h2>
              <p className="text-gray-300 text-base sm:text-lg">Fill out the form and we&apos;ll get back to you within 24 hours</p>
            </div>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-10" data-readdy-form id={`area-quote-form-${areaSlug}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-[#1a1a1a] mb-2">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors" placeholder="John Smith" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-[#1a1a1a] mb-2">Phone Number *</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors" placeholder="(559) 555-1234" />
                </div>
              </div>
              <div className="mb-4 sm:mb-6">
                <label htmlFor="email" className="block text-sm font-bold text-[#1a1a1a] mb-2">Email Address *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors" placeholder="john@example.com" />
              </div>
              <div className="mb-4 sm:mb-6">
                <label htmlFor="urgency" className="block text-sm font-bold text-[#1a1a1a] mb-2">Urgency *</label>
                <select id="urgency" name="urgency" value={formData.urgency} onChange={handleFormChange} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors bg-white cursor-pointer">
                  <option value="">Select urgency level...</option>
                  <option value="Emergency — Need help now">Emergency — Need help now</option>
                  <option value="Urgent — Within 24 hours">Urgent — Within 24 hours</option>
                  <option value="Soon — Within a few days">Soon — Within a few days</option>
                  <option value="Planning — Just getting a quote">Planning — Just getting a quote</option>
                </select>
              </div>
              <div className="mb-6 sm:mb-8">
                <label htmlFor="message" className="block text-sm font-bold text-[#1a1a1a] mb-2">Message *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleFormChange} maxLength={500} rows={4} required className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors resize-none" placeholder={`Tell us about your electrical needs in ${area}...`}></textarea>
              </div>
              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <span className="text-sm font-medium text-green-700">Thank you! We&apos;ll contact you within 24 hours.</span>
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-500 text-xl"></i>
                  <span className="text-sm font-medium text-red-700">Something went wrong. Please try again.</span>
                </div>
              )}
              <button type="submit" disabled={formStatus === 'sending'} className="w-full bg-[#FFD700] text-[#1a1a1a] py-4 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-[#FFC700] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                {formStatus === 'sending' ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                ) : (
                  <><i className="ri-send-plane-fill"></i> Get Your Free Quote in {area}</>
                )}
              </button>
            </form>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-4">FAQs</span>
              <h2 className="text-3xl sm:text-5xl font-black text-[#1a1a1a] mb-4">
                {area.toUpperCase()} ELECTRICIAN<br />QUESTIONS ANSWERED
              </h2>
            </div>
            <FaqAccordion faqs={faqs} />
          </div>
        </section>

        {/* Nearby Areas Internal Links */}
        <section className="py-12 sm:py-16 bg-[#f5f5f5]">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-black text-[#1a1a1a] mb-2 text-center">ALSO SERVING NEARBY AREAS</h2>
            <p className="text-gray-500 text-center mb-8 text-sm">Our licensed electricians cover the entire Central Valley region</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {nearbyAreas.map((na) => (
                <Link
                  key={na.slug}
                  to={`/areas/${na.slug}`}
                  className="bg-white rounded-xl p-4 text-center hover:border-[#FFD700] border-2 border-transparent transition-all group cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-map-pin-2-fill text-[#FFD700] text-2xl group-hover:scale-110 transition-transform"></i>
                  </span>
                  <p className="font-bold text-[#1a1a1a] text-sm">Electrician in</p>
                  <p className="font-black text-[#1a1a1a] text-base">{na.name}</p>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/" className="text-[#FFD700] font-bold hover:underline text-sm">
                ← Back to Fresno Homepage
              </Link>
            </div>
          </div>
        </section>

        {/* Certifications scroll */}
        <section className="py-8 sm:py-12 bg-white overflow-hidden">
          <div className="flex items-center gap-16 animate-scroll">
            {[...certifications, ...certifications].map((cert, index) => (
              <div key={index} className="flex items-center gap-4 whitespace-nowrap">
                <div className="text-center">
                  <div className="font-black text-[#1a1a1a] text-lg">{cert.name}</div>
                  <div className="text-gray-600 text-sm">{cert.description}</div>
                </div>
                {index < certifications.length * 2 - 1 && <div className="w-2 h-2 bg-[#FFD700] rounded-full"></div>}
              </div>
            ))}
          </div>
        </section>

        {/* Mobile sticky banner */}
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
          <a href="#get-quote" className="call-banner flex items-center justify-center gap-3 bg-[#FFD700] text-[#1a1a1a] py-4 px-6 w-full cursor-pointer">
            <span className="w-6 h-6 flex items-center justify-center bolt-icon">
              <i className="ri-flashlight-fill text-xl"></i>
            </span>
            <span className="font-black text-base uppercase tracking-widest whitespace-nowrap">Free Quote — {area}</span>
            <span className="w-6 h-6 flex items-center justify-center bolt-icon">
              <i className="ri-flashlight-fill text-lg"></i>
            </span>
          </a>
        </div>

        <EmergencyModal isOpen={showEmergency} onClose={() => setShowEmergency(false)} />
        <Footer />
      </div>
    </>
  );
};

function FaqAccordion({ faqs }: { faqs: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div key={i} className="border-2 border-gray-100 rounded-xl overflow-hidden transition-all" style={{ borderColor: open === i ? '#FFD700' : undefined }}>
          <button className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-[#fffbea] transition-colors cursor-pointer" onClick={() => setOpen(open === i ? null : i)}>
            <span className="font-bold text-[#1a1a1a] text-sm sm:text-base">{faq.q}</span>
            <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-[#FFD700] text-[#1a1a1a]">
              <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`}></i>
            </span>
          </button>
          {open === i && (
            <div className="px-6 pb-5 bg-[#fffbea]">
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{faq.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default AreaTemplate;
